import { Box } from "@material-ui/core";
import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { isCorpTenant } from "@capone/common";
import { useDeviceTypes, LoadingPopup, B2BSpinner } from "halifax";
import { Route, RouteComponentProps } from "react-router-dom";
import { CallState, REVIEW_DETAILS_VR_CHECKOUT } from "redmond";
import { AvailabilityResultEnum } from "redmond/apis/tysons/vacation-rentals";
import { ClientContext } from "../../App";
import {
  AVAILABLE,
  CORE_VACATION_RENTALS_SHOP,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
} from "../../context/experiments";
import {
  PATH_VACATION_RENTALS_BOOK,
  PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
} from "../../utils/paths";
import { VacationRentalBookBookConnectorProps } from "./container";
import { goToShop } from "../shop/utils/queryStringHelpers";
import { PORTAL_TITLE, VACATION_RENTAL_CHECKOUT_TITLE } from "./textConstants";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { VRBookingErrorModal } from "./components/VRBookingErrorModal";
import { BookingInProgressModal } from "./components/BookingInProgressModal";
import { BookingSuccessModal } from "./components/BookingSuccessModal";
import { config } from "../../api/config";
import { VacationRentalShopCallState } from "../shop/reducer";

const CorpDesktopVacationRentalsBookWorkflow = React.lazy(
  () =>
    import("./components/capone-corporate/DesktopVacationRentalsBookWorkflow")
);
const CorpMobileVacationRentalsBookWorkflow = React.lazy(
  () =>
    import("./components/capone-corporate/MobileVacationRentalsBookWorkflow")
);
const ConsumerDesktopVacationRentalsBookWorkflow = React.lazy(
  () => import("./components/capone/DesktopVacationRentalsBookWorkflow")
);
const ConsumerMobileVacationRentalsBookWorkflow = React.lazy(
  () => import("./components/capone/MobileVacationRentalsBookWorkflow")
);

export interface IVacationRentalBookProps
  extends VacationRentalBookBookConnectorProps,
  RouteComponentProps { }

export const VacationRentalBook = (props: IVacationRentalBookProps) => {
  const {
    history,
    location,
    selectedHome,
    vacationRentalShopCallState,
    travelers,
    userPassengerCallState,
    viewedVacationRentalDetailsProperties,
    fetchVacationRentalShop,
  } = props;

  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const [primaryTravelerId, setPrimaryTravelerId] = useState<
    string | undefined
  >(undefined);
  const [searchedPrimaryTraveler, setSearchedPrimaryTraveler] = useState(false);

  const userInfo = useContext(ClientContext).sessionInfo?.userInfo;

  const expState = useExperiments();

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment]
  );

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer]
  );

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(() => {
    return creditAndOfferStackingExperimentV1 === AVAILABLE;
  }, [creditAndOfferStackingExperimentV1]);

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(() => {
    return travelCreditHistoryExperiment === AVAILABLE;
  }, [travelCreditHistoryExperiment]);

  const DesktopVacationRentalsBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpDesktopVacationRentalsBookWorkflow
    : ConsumerDesktopVacationRentalsBookWorkflow;

  const MobileVacationRentalsBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpMobileVacationRentalsBookWorkflow
    : ConsumerMobileVacationRentalsBookWorkflow;

  const isReplatformShopEnabled =
    getExperimentVariant(expState.experiments, CORE_VACATION_RENTALS_SHOP) ===
    AVAILABLE;

  const areExperimentsLoaded =
    [CallState.Success, CallState.Failed].includes(expState.callState) ||
    expState.experiments.length > 0;

  // Show a loading state while we (re)load the listing from the URL, so the
  // user never sees a blank or half-loaded page. Failures fall through to the
  // error modal below.
  const isVacationRentalBookLoading =
    (selectedHome == null ||
      vacationRentalShopCallState === VacationRentalShopCallState.InProcess) &&
    vacationRentalShopCallState !== VacationRentalShopCallState.Failed;

  // The replatformed shop doesn't fill this module's store, so load the listing
  // from the URL here. In the legacy flow the shop already did, so we skip the
  // extra fetch (and its spinner) unless nothing is loaded. Runs once per visit,
  // after experiments load — they tell us which flow we're in.
  const hasHydratedFromUrl = useRef(false);
  useEffect(() => {
    if (!areExperimentsLoaded || hasHydratedFromUrl.current) {
      return;
    }

    const params = new URLSearchParams(history.location.search);
    const hasRequiredBookingParams =
      params.get("listingId") != null &&
      params.get("fromDate") != null &&
      params.get("untilDate") != null &&
      params.get("adultsCount") != null;

    if (hasRequiredBookingParams && (isReplatformShopEnabled || !selectedHome)) {
      hasHydratedFromUrl.current = true;
      fetchVacationRentalShop(history, {
        overrideStateByQueryParams: true,
        fetchListingCalendar: true,
      });
    } else if (!hasRequiredBookingParams && !selectedHome) {
      // Nothing to load and no params to load it with — go to the shop instead.
      goToShop({ history });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-hydrate once per book visit, once experiments resolve
  }, [areExperimentsLoaded]);

  // If the listing can't be booked for these dates (unavailable, or the fetch
  // failed), send the user back to the shop, which shows the right messaging.
  // We only act on the fetch we just started, so a leftover cached listing
  // never bounces a user who picked valid dates.
  const prevShopCallState = useRef(vacationRentalShopCallState);
  useEffect(() => {
    const prevCallState = prevShopCallState.current;
    prevShopCallState.current = vacationRentalShopCallState;

    if (prevCallState !== VacationRentalShopCallState.InProcess) {
      return;
    }

    const fetchFailed =
      vacationRentalShopCallState === VacationRentalShopCallState.Failed;
    const notBookable =
      vacationRentalShopCallState === VacationRentalShopCallState.Success &&
      (selectedHome == null ||
        selectedHome.availability.AvailabilityResult !==
          AvailabilityResultEnum.Available);

    if (fetchFailed || notBookable) {
      goToShop({ history });
    }
  }, [vacationRentalShopCallState, selectedHome]);

  useEffect(() => {
    document.title = VACATION_RENTAL_CHECKOUT_TITLE;
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  // Fire once the listing is actually loaded, not on mount. Coming from the
  // replatformed shop this module's store starts empty and is hydrated from the
  // URL above, so a mount-time check saw `selectedHome == null` and skipped the
  // event entirely. In the legacy flow the shop already populated the store, so
  // this still fires on the first pass.
  const hasTrackedReviewDetails = useRef(false);
  useEffect(() => {
    if (hasTrackedReviewDetails.current || !selectedHome) {
      return;
    }
    hasTrackedReviewDetails.current = true;
    trackEvent({
      eventName: REVIEW_DETAILS_VR_CHECKOUT,
      properties: {
        ...viewedVacationRentalDetailsProperties.properties,
      },
      encryptedProperties: [
        ...viewedVacationRentalDetailsProperties.encryptedProperties,
      ],
    });
  }, [selectedHome, viewedVacationRentalDetailsProperties]);

  // Preselect primary traveler if the logged in user's information matches a traveler.
  // Only the id is latched — the object itself is resolved from the live list below,
  // so editing that traveler doesn't leave a stale pre-edit copy behind.
  useEffect(() => {
    if (userPassengerCallState === CallState.Success) {
      const savedPrimaryTraveler = travelers.find(
        (t) =>
          t.givenName === userInfo?.firstName &&
          t.surname === userInfo?.lastName
      );
      setSearchedPrimaryTraveler(true);
      if (!searchedPrimaryTraveler) {
        setPrimaryTravelerId(savedPrimaryTraveler?.id);
      }
    }
  }, [travelers, userPassengerCallState, searchedPrimaryTraveler]);

  const primaryTraveler = travelers.find((t) => t.id === primaryTravelerId);

  // Remount the desktop workflow when the searched dates/guests change (the only
  // place they're editable). Changing them re-fetches the shop listing and resets
  // the booking store (redoSearch); remounting resets the workflow's local state and
  // re-runs its mount effects (customer details / userInfo, initial price quote) so
  // the page ends up consistent — everything a reload would do, minus the reload.
  // Only date/guest params are included, so mobile's bookStep changes never remount.
  const bookSearchParams = new URLSearchParams(location.search);
  const desktopWorkflowKey = [
    bookSearchParams.get("fromDate"),
    bookSearchParams.get("untilDate"),
    bookSearchParams.get("adultsCount"),
    bookSearchParams.get("childrenCount"),
    bookSearchParams.get("petsCount"),
  ].join("|");

  return (
    <Box
      className={clsx(
        "vacation-rental-book-root",
        {
          confirm:
            location.pathname === PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
        },
        { mobile: matchesMobile }
      )}
    >
      <Route path={PATH_VACATION_RENTALS_BOOK} exact>
        <Box className="vacation-rental-book-container">
          {matchesDesktop && (
            <Suspense>
              <DesktopVacationRentalsBookWorkflow
                key={desktopWorkflowKey}
                isTreesModalExperiment={isTreesModalExperiment}
                isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
                isCreditAndOfferStackingExperimentV1={
                  isCreditAndOfferStackingExperimentV1
                }
                isTravelCreditHistoryExperiment={
                  isTravelCreditHistoryExperiment
                }
                primaryTraveler={primaryTraveler}
              />
            </Suspense>
          )}
          {matchesMobile && (
            <Suspense>
              <MobileVacationRentalsBookWorkflow
                isTreesModalExperiment={isTreesModalExperiment}
                isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
                isTravelWalletCreditsExperiment={
                  isTravelWalletCreditsExperiment
                }
                isCreditAndOfferStackingExperimentV1={
                  isCreditAndOfferStackingExperimentV1
                }
                isTravelCreditHistoryExperiment={
                  isTravelCreditHistoryExperiment
                }
                primaryTraveler={primaryTraveler}
              />
            </Suspense>
          )}
        </Box>
        <VRBookingErrorModal />
        <BookingInProgressModal />
        <LoadingPopup
          indicatorSize="small"
          indicator={B2BSpinner}
          open={isVacationRentalBookLoading}
          popupSize="small"
          message="Checking availability"
        />
      </Route>
      <Route path={[PATH_VACATION_RENTALS_BOOK_CONFIRMATION]}>
        <BookingSuccessModal isTreesModalExperiment={isTreesModalExperiment} />
      </Route>
    </Box>
  );
};
